// src/components/ui/Layout/AdaptiveLayout.tsx
import React, { ReactNode } from 'react';
import { useResponsive } from '@/hooks/useResponsive';
import { Container } from './Container';
import styles from './AdaptiveLayout.module.css';

export interface AdaptiveLayoutProps {
  children: ReactNode;
  // Компоненты для разных типов устройств
  mobileContent?: ReactNode;
  tabletContent?: ReactNode;
  desktopContent?: ReactNode;
  // Настройки контейнера
  containerSize?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  // Дополнительные элементы
  sidebar?: ReactNode;
  sidebarPosition?: 'left' | 'right';
  header?: ReactNode;
  footer?: ReactNode;
  className?: string;
  withBottomPadding?: boolean;
}

/**
 * Адаптивный макет, который автоматически отображает разный контент
 * в зависимости от типа устройства (мобильное, планшет, десктоп)
 */
export const AdaptiveLayout: React.FC<AdaptiveLayoutProps> = ({
  children,
  mobileContent,
  tabletContent,
  desktopContent,
  containerSize = 'xl',
  sidebar,
  sidebarPosition = 'left',
  header,
  footer,
  className = '',
  withBottomPadding = true
}) => {
  // Используем хук для определения типа устройства
  const { isMobile, isTablet, isDesktop } = useResponsive();
  
  // Определяем основной контент в зависимости от устройства
  const getContent = () => {
    if (isMobile && mobileContent) {
      return mobileContent;
    } else if (isTablet && tabletContent) {
      return tabletContent;
    } else if (isDesktop && desktopContent) {
      return desktopContent;
    }
    
    // Если специфический контент не предоставлен, используем общий
    return children;
  };
  
  // Определяем класс для контента с учетом нижнего отступа если нужен
  const contentClass = (isMobile && withBottomPadding) 
    ? `${styles.content} ${styles.contentWithBottomPadding}` 
    : styles.content;
  
  // Подготавливаем макет с сайдбаром
  const renderWithSidebar = () => {
    if (!sidebar) return getContent();
    
    // На мобильных устройствах не отображаем сайдбар
    if (isMobile) {
      return getContent();
    }
    
    // Определяем класс для сайдбара в зависимости от позиции
    const sidebarClass = `${styles.sidebar} ${sidebarPosition === 'left' ? styles.sidebarLeft : styles.sidebarRight}`;
    
    // В зависимости от позиции сайдбара формируем разную структуру
    if (sidebarPosition === 'left') {
      return (
        <div className={styles.flexContainer}>
          <div className={sidebarClass}>{sidebar}</div>
          <div className={styles.mainContent}>{getContent()}</div>
        </div>
      );
    } else {
      return (
        <div className={styles.flexContainer}>
          <div className={styles.mainContent}>{getContent()}</div>
          <div className={sidebarClass}>{sidebar}</div>
        </div>
      );
    }
  };
  
  return (
    <div className={`${styles.layout} ${className}`}>
      {/* Общий хедер, если передан */}
      {header && <div className="w-full">{header}</div>}
      
      {/* Основной контент */}
      <div className={contentClass}>
        <Container size={containerSize}>
          {renderWithSidebar()}
        </Container>
      </div>
      
      {/* Общий футер, если передан */}
      {footer && <div className="w-full">{footer}</div>}
    </div>
  );
};

export default AdaptiveLayout;